import { Link, useParams } from 'react-router';
import { Button, EmptyState } from '@ecobox/ui';
import { useDriverData } from '../../hooks/useDriver';
import { useRunBranch } from '../../hooks/useRunBranch';
import { usePermissions } from '../../sessions';
import { DeliveryStop } from './DeliveryStop';
import { PickupStop } from './PickupStop';

/** One stop of a run: a delivery or a pickup, by the stop's kind. */
export function StopPage() {
  const { runId = '', stopId = '' } = useParams();
  const data = useDriverData();
  const perms = usePermissions();
  const run = data.runs.find((r) => r.id === runId);
  const stop = data.stopsOf(runId)?.find((s) => s.id === stopId);
  useRunBranch(run?.branchId);

  if (!run || !stop) {
    return (
      <EmptyState
        title={data.loading ? 'Loading the stop…' : 'This stop is not on your phone'}
        description={
          data.loading
            ? 'It opens as soon as it has loaded.'
            : 'It may have been removed from the run. Go back to the run.'
        }
        action={
          <Link to={run ? `/run/${run.id}` : '/today'}>
            <Button size="touch">{run ? 'Back to the run' : 'Back to Today'}</Button>
          </Link>
        }
      />
    );
  }
  const problems = data.problemsOf(run.id);
  if (stop.kind === 'delivery') {
    if (!perms.has('finalmile.deliver')) return <NotYours what="Delivering" runId={run.id} />;
    return (
      <DeliveryStop
        run={run}
        stop={stop}
        shipment={stop.shipmentId ? data.shipments.get(stop.shipmentId) : undefined}
        problems={problems}
      />
    );
  }
  if (!perms.has('pickup.complete')) return <NotYours what="Pickups" runId={run.id} />;
  return (
    <PickupStop
      run={run}
      stop={stop}
      request={stop.pickupRequestId ? data.pickups.get(stop.pickupRequestId) : undefined}
      problems={problems}
    />
  );
}

function NotYours({ what, runId }: { what: string; runId: string }) {
  return (
    <EmptyState
      title={`${what} is not part of your role`}
      description="Ask your manager if you need it."
      action={
        <Link to={`/run/${runId}`}>
          <Button size="touch">Back to the run</Button>
        </Link>
      }
    />
  );
}
